import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AppUnderlinedTitle } from "../../../../../../../shared/components/AppUnderlinedTitle";
import { PlusIcon } from "../../../../../../../shared/icons/PlusIcon";
import { AppButton } from "../../../../../../../shared/components/AppButton";
import { useAppSelector } from "../../../../../../../services/redux/hooks/use-selector";
import {
  selectAssistantMeta,
  selectAssistantValue,
} from "../../../../../redux/selectors";
import { useUpdateStorageValue } from "../../../../../../../app/state/storageObjectSlice/hook/use-update-storage-value";
import { setAssistantValue } from "../../../../../redux/slice";
import { getUpdatedValue } from "../../../../../helpers/getUpdatedValue";
import { generateId } from "../../../../../../../shared/utils/generateId";
import { TDirection } from "../../../../../types";
import { TMenuIds } from "../../../../../../../app/state/menuSlice/types";
import { TEntityInfo } from "../../../../../../../common/types/utility";
import styles from "./styles.module.css";

interface Props {
  direction: TDirection;
  handleClick: (
    event: React.MouseEvent,
    menuId: TMenuIds,
    info: TEntityInfo | null,
  ) => void;
}

export const ModalHeader = ({ direction, handleClick }: Props) => {
  const meta = useAppSelector(selectAssistantMeta);
  const assistantValue = useAppSelector(selectAssistantValue);
  const { saveValue } = useUpdateStorageValue("assistants", setAssistantValue);
  const { t } = useTranslation();

  const toolsCount = useMemo(() => {
    const count =
      (meta?.tools?.assistants?.length ?? 0) +
      (meta?.tools?.skills?.length ?? 0);
    return count;
  }, [meta?.tools]);

  const handleAddAssistant = () => {
    if (!assistantValue) return;
    const newTools = {
      ...meta?.tools,
      assistants: [
        ...(meta?.tools?.assistants ?? []),
        { id: generateId(), type: "assistant" as const, ref: "", when: "" },
      ],
    };
    const newValue = getUpdatedValue(
      assistantValue,
      {},
      {
        ...meta,
        tools: newTools,
      },
    );
    saveValue(newValue);
  };

  const handleAddSkill = () => {
    if (!assistantValue) return;
    const newTools = {
      ...meta?.tools,
      skills: [
        ...(meta?.tools?.skills ?? []),
        {
          id: generateId(),
          name: "",
          friendly_name: "",
          description: "",
          path: "",
          tags: [] as string[],
        },
      ],
    };
    const newValue = getUpdatedValue(
      assistantValue,
      {},
      {
        ...meta,
        tools: newTools,
      },
    );
    saveValue(newValue);
  };

  return (
    <AppUnderlinedTitle
      children={
        <div className={styles.container}>
          <AppButton
            buttonType="text"
            icon={<PlusIcon />}
            onClick={handleAddAssistant}
            disabled={toolsCount === 10}
          >
            {t("editor.add_module_assistants")}
          </AppButton>
          <span className={styles["divider"]}></span>
          <AppButton
            buttonType="text"
            icon={<PlusIcon />}
            onClick={handleAddSkill}
            disabled={toolsCount === 10}
          >
            {t("agents.skills")}
          </AppButton>
        </div>
      }
      className={styles["modal-header"]}
    />
  );
};
